import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ShieldCheck, ArrowLeft, CheckCircle2, Loader2, AlertCircle, Home } from "lucide-react";
import { Seo, serviceJsonLd } from "../components/Seo";

export default function SecurityBooking() {
  const [form, setForm] = useState({ name: "", phone: "", postal_code: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim() || !form.postal_code.trim()) {
      setError("Fyll i namn, telefonnummer och postnummer.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", { ...form, service: "sakerhet_hembesok", tv_brand: "", tv_providers: [] });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Säker på nätet – boka hembesök"
        description="Boka ett personligt hembesök där vi går igenom internetsäkerheten direkt på dina egna apparater – virusskydd, bluff-sms, säkra lösenord och BankID."
        path="/sakerhet/hembesok"
        jsonLd={serviceJsonLd({ name: "Säker på nätet – hembesök", description: "Personligt hembesök om internetsäkerhet.", path: "/sakerhet/hembesok" })}
      />
      <Link to="/sakerhet" data-testid="secbooking-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <ShieldCheck size={44} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> Säker på nätet – Hembesök
      </h1>

      {done ? (
        <div data-testid="booking-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-8 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för din intresseanmälan!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Vi har tagit emot dina uppgifter och ringer upp dig på det telefonnummer du angav för att boka en tid som passar dig.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <Home size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <>
          <div className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
            <p className="text-lg leading-relaxed text-[#1A1A1A]">
              Vi kommer hem till dig och går igenom säkerheten direkt på dina egna apparater –
              telefon, surfplatta och dator.
            </p>
            <p className="text-lg leading-relaxed text-[#1A1A1A] mt-4">
              Vi kollar ditt virusskydd, pratar om bluff-sms, säkra lösenord
              och hur du använder BankID tryggt.
            </p>
            <p className="text-lg leading-relaxed text-[#1A1A1A] mt-4">
              Allt i din egen takt.
            </p>
          </div>

          <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Fyll i dina uppgifter</h2>
            <p className="text-base text-[#4A4A4A] mt-2">Så ringer vi upp dig och bokar en tid. Det tar bara en minut.</p>

            <div className="space-y-6 mt-6">
              <div>
                <label htmlFor="b-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
                <input id="b-name" data-testid="booking-name" value={form.name} onChange={set("name")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
              </div>
              <div>
                <label htmlFor="b-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
                <input id="b-phone" data-testid="booking-phone" type="tel" value={form.phone} onChange={set("phone")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
              </div>
              <div>
                <label htmlFor="b-postal" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Postnummer</label>
                <input id="b-postal" data-testid="booking-postal" value={form.postal_code} onChange={set("postal_code")}
                  className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="XXX XX" />
              </div>

              {error && (
                <p data-testid="booking-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                  <AlertCircle size={22} /> {error}
                </p>
              )}

              <button data-testid="booking-submit" type="submit" disabled={submitting}
                className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
                {submitting ? <Loader2 className="animate-spin" size={26} /> : <ShieldCheck size={26} strokeWidth={2.5} />}
                {submitting ? "Skickar…" : "Skicka min intresseanmälan"}
              </button>
              <p className="text-base text-[#4A4A4A] text-center">Du bokar inget bindande här – vi ringer upp och bestämmer en tid tillsammans.</p>
              <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
                Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
              </p>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
